import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import {
  useAddressBookByCenter,
  useAddressBookSearch,
  useDeleteAddressMutation,
} from '../../../services/addressBook'
import AddressBookView from '../../../ui/modules/AddressBook'
import { useDestinationCountries } from '../../../services/destinationCountries'
import { STEPS } from '../../../common/constants'
import AddressEdition from './AddressEdition'
import ModalLayout from '../../../ui/modals/layout'
import { Alert, Button } from '@mui/material'
import useTranslations from '../../../i18n/useTranslations'

const propTypes = {
  centerCode: PropTypes.string,
  currentStep: PropTypes.string,
  selectDirection: PropTypes.func,
  currentAddressBook: PropTypes.object,
}

const AddressBook = ({
  currentAddressBook,
  currentStep,
  centerCode,
  selectDirection,
}) => {
  const [editingAddress, setEditingAddress] = useState(null)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(currentAddressBook ?? null)
  const [validOrigin, setValidOrigin] = useState(false)
  const [addressToDelete, setAddressToDelete] = useState()
  const [hasPhone, setHasPhone] = useState(false)
  const [pageManagment, setPageManagment] = useState({
    page: 0,
    pageLimit: 5,
  })

  const t = useTranslations()

  const { data: countries } = useDestinationCountries()

  const addressesByCenter = useAddressBookByCenter(
    {
      centreCode: centerCode,
      pageLimit: pageManagment.pageLimit,
      pageOffset: pageManagment.page,
    },
    { enabled: search === '' },
  )
  const addressesBySearch = useAddressBookSearch({
    centreCode: centerCode,
    pageLimit: pageManagment.pageLimit,
    pageOffset: pageManagment.page,
    searchFilterValue: search,
  })
  const addresses = search === '' ? addressesByCenter : addressesBySearch
  const deleteAddressMutation = useDeleteAddressMutation(addresses.refetch)

  const handleSubmitDelete = clientAddressCode => {
    deleteAddressMutation.mutate({
      clientCenterCode: centerCode,
      clientAddressCode: clientAddressCode,
    })
    setAddressToDelete({ delete: true })
  }

  useEffect(() => {
    if (currentAddressBook !== null) {
      if (currentStep === STEPS.FROM && currentAddressBook !== true)
        setValidOrigin(
          currentAddressBook?.country === 'ES' ||
            currentAddressBook?.country === 'PT',
        )
      else setValidOrigin(true)
      if (!currentAddressBook?.phone?.length && currentAddressBook !== true)
        setHasPhone(false)
      else setHasPhone(true)
      setSelected({...selected, ...currentAddressBook})
    }
  }, [currentAddressBook])

  if (editingAddress !== null) {
    return (
      <AddressEdition
        initialData={editingAddress}
        currentStep={currentStep}
        onGoBack={() => setEditingAddress(null)}
        countries={countries}
        onUpdateAddress={_address => {
          setSelected({..._address.data, ..._address})
          addresses.refetch()
        }}
      />
    )
  }

  return (
    <>
      <ModalLayout
        isOpen={addressToDelete}
        title={t('action:delete-address-my-agenda-header')}
        content={
          !addressToDelete?.delete ? (
            t('action:delete-address-my-agenda-body')
          ) : (
            <Alert
              type="success"
              extraClass="is-deliveryPointMainError"
              iconAlignment="top"
            >
              {t('action:delete-address-my-agenda-body-success')}
            </Alert>
          )
        }
        actions={
          <>
            {!addressToDelete?.delete ? (
              <Button
                type="button"
                size="small"
                variant="outlined"
                onClick={() => setAddressToDelete(null)}
              >
                {t('action:cancel')}
              </Button>
            ) : null}
            <Button
              size="small"
              onClick={() => {
                if (!addressToDelete?.delete) {
                  handleSubmitDelete(addressToDelete)
                  if (selected?.id === addressToDelete) setSelected(null)
                } else setAddressToDelete(null)
              }}
            >
              {!addressToDelete?.delete
                ? t('action:confirm')
                : t('action:close')}
            </Button>
          </>
        }
      />
      <AddressBookView
        onClickEditAddress={address => {
          setEditingAddress(address)
        }}
        onClickDeleteAddress={address => {
          setAddressToDelete(address.rawData.client_address_code)
        }}
        selected={selected}
        validOrigin={validOrigin}
        hasPhone={hasPhone}
        addresses={addresses?.data?.data ?? []}
        isLoading={addresses.isLoading}
        page={pageManagment.page}
        rowsPerPage={addresses?.data?.pagination?.itemsPerPage ?? 0}
        count={addresses?.data?.pagination?.totalCount ?? 0}
        onAddSearchFilter={debouncedSearch => {
          // TODO calcular cuándo tenemos que pedir las direcciones desde la
          // página 0.
          // Actualmente, si avanzamos 3 páginas y luego realizamos una
          // búsqueda, esa búsqueda se realizará desde la página 3 en lugar de
          // desde la 0.
          setSearch(debouncedSearch)
        }}
        onChange={address => {
          if (currentStep === STEPS.FROM)
            setValidOrigin(address.country === 'ES' || address.country === 'PT')
          else setValidOrigin(true)
          if (!address.phone?.length) setHasPhone(false)
          else setHasPhone(true)
          selectDirection(address)
          setSelected(address)
        }}
        onPageChange={(_event, page) => {
          setPageManagment(pageManagment => ({ ...pageManagment, page }))
        }}
        onRowsPerPageChange={event => {
          const pageLimit = event.target.value
          setPageManagment({
            page: 0,
            pageLimit,
          })
        }}
      />
    </>
  )
}

AddressBook.propTypes = propTypes

export default AddressBook
